import axios from "axios";
import { useEffect, useState } from "react";
import { Person } from "react-bootstrap-icons";

import { ApiConnectionString } from "../../../constants/ApiConntection";

import SettingsCard from "./SettingsCard";
import SectionMessage from "./SectionMessage";

import {
  createEmptyMessage,
  displayValue,
  getErrorMessage,
} from "../settingsUtils";

const createProfileForm = (profile) => ({
  practiceName:
    profile?.practiceName ??
    profile?.PracticeName ??
    "",
  firstName:
    profile?.firstName ??
    profile?.FirstName ??
    "",
  lastName:
    profile?.lastName ??
    profile?.LastName ??
    "",
  email:
    profile?.email ??
    profile?.Email ??
    "",
});

export default function ProfileSettings({
  profile,
  token,
  onProfileUpdated,
}) {
  const [form, setForm] = useState(() => createProfileForm(profile));
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(createEmptyMessage);

  useEffect(() => {
    setForm(createProfileForm(profile));
  }, [profile]);

  const clearMessage = () => {
    setMessage(createEmptyMessage());
  };

  const setError = (error) => {
    setMessage({
      error,
      success: "",
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const startEditing = () => {
    clearMessage();
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setForm(createProfileForm(profile));
    clearMessage();
    setIsEditing(false);
  };

  const saveChanges = async () => {
    if (!form.firstName.trim()) {
      setError("First name is required.");
      return;
    }

    if (!form.lastName.trim()) {
      setError("Last name is required.");
      return;
    }

    try {
      setIsSaving(true);
      clearMessage();

      const response = await axios.put(
        `${ApiConnectionString}/api/myProfile/profile`,
        {
          practiceName: form.practiceName.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const returnedProfile =
        response.data && typeof response.data === "object"
          ? response.data
          : {};

      const updatedProfile = {
        ...profile,
        ...returnedProfile,
        practiceName:
          returnedProfile.practiceName ??
          returnedProfile.PracticeName ??
          form.practiceName.trim(),
        firstName:
          returnedProfile.firstName ??
          returnedProfile.FirstName ??
          form.firstName.trim(),
        lastName:
          returnedProfile.lastName ??
          returnedProfile.LastName ??
          form.lastName.trim(),
      };

      onProfileUpdated(updatedProfile);
      setForm(createProfileForm(updatedProfile));
      setIsEditing(false);

      setMessage({
        error: "",
        success: "Profile updated successfully.",
      });
    } catch (error) {
      console.error(error);

      setError(
        getErrorMessage(error, "Failed to save profile changes.")
      );
    } finally {
      setIsSaving(false);
    }
  };

  const practiceName =
    profile?.practiceName ??
    profile?.PracticeName;

  const firstName =
    profile?.firstName ??
    profile?.FirstName;

  const lastName =
    profile?.lastName ??
    profile?.LastName;

  const email =
    profile?.email ??
    profile?.Email;

  return (
    <SettingsCard
      icon={<Person />}
      title="Profile"
      description="Update your practice and personal information."
    >
      <div className="settings-form">
        <SectionMessage message={message} />

        <div>
          <label htmlFor="settings-practice-name">
            Practice name
          </label>

          {!isEditing ? (
            <div className="settings-readonly-value">
              {displayValue(practiceName)}
            </div>
          ) : (
            <input
              id="settings-practice-name"
              type="text"
              name="practiceName"
              value={form.practiceName}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="settings-grid">
          <div>
            <label htmlFor="settings-first-name">
              First name
            </label>

            {!isEditing ? (
              <div className="settings-readonly-value">
                {displayValue(firstName)}
              </div>
            ) : (
              <input
                id="settings-first-name"
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
              />
            )}
          </div>

          <div>
            <label htmlFor="settings-last-name">
              Last name
            </label>

            {!isEditing ? (
              <div className="settings-readonly-value">
                {displayValue(lastName)}
              </div>
            ) : (
              <input
                id="settings-last-name"
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
              />
            )}
          </div>
        </div>

        <div>
          <label>Email address</label>

          <div className="settings-readonly-value">
            {displayValue(email)}
          </div>

          <small>Your email address cannot be changed here.</small>
        </div>

        <div className="settings-actions">
          {!isEditing ? (
            <button type="button" onClick={startEditing}>
              Edit
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={saveChanges}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save changes"}
              </button>

              <button
                type="button"
                onClick={cancelEditing}
                disabled={isSaving}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </SettingsCard>
  );
}